import React from 'react'
import styled from '@emotion/styled'
import { PillButton } from '@mobi/component-library/Base/Buttons'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { TabsScrollContainerStyled } from '@mobi/component-library/Navigation/Tabs/Components/Common.styles'

/** Main wrapping component to be used with <TabPillButton /> children */
export const TabsPill: React.FC<{
  children: React.ReactNode
  bottomBorder?: 'soft' | 'hard' | 'none'
  hasPaddingInline?: boolean
}> = ({ children, bottomBorder = 'soft', hasPaddingInline = true }) => {
  return (
    <TabsPillWrapperStyled data-bottom-border={bottomBorder} data-inline-padding={hasPaddingInline}>
      <TabsScrollContainerStyled>{children}</TabsScrollContainerStyled>
    </TabsPillWrapperStyled>
  )
}

/** Requires wrapping <TabsPill /> parent component */
export const TabPillButton = ({
  content,
  onClick,
  isSelected,
}: {
  content: React.ReactNode
  onClick: React.MouseEventHandler<HTMLButtonElement>
  isSelected: boolean
}) => {
  const elRef = React.useRef<HTMLLIElement>(null)

  React.useEffect(() => {
    if (!isSelected) return
    const timerId = setTimeout(() => {
      elRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' })
    }, 200)
    return () => clearTimeout(timerId)
  }, [isSelected])

  return (
    <li ref={elRef}>
      <PillButton size='xs' onClick={onClick} color={isSelected ? 'primary' : 'secondary_color'}>
        {content}
      </PillButton>
    </li>
  )
}

// ======
// Styles
// ======

const TabsPillWrapperStyled = styled.div({
  overflow: 'hidden',
  background: colors.white,

  '&[data-bottom-border="soft"]': {
    borderBottom: `0.05rem solid ${colors.neutral[200]}`,
  },
  '&[data-bottom-border="hard"]': {
    borderBottom: `0.05rem solid ${colors.neutral[400]}`,
  },
  '&[data-inline-padding="false"]': {
    '> ul': {
      paddingInline: 0,
    },
  },

  '*': { boxSizing: 'border-box' },

  '> ul': {
    gap: spacing.smx1,
    alignItems: 'center',
  },

  '> ul > li': {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'center',

    '> button': {
      fontSize: font.size.md.fontSize,
      letterSpacing: font.size.md.letterSpacing,
      lineHeight: font.size.md.lineHeight,
      flexShrink: 0,
    },
  },
})
